import React, { useMemo } from "react";
import { Badge, Modal, ModalBody, ModalHeader, Table } from "reactstrap";
import dayjs from "dayjs";
import Loaders from "components/Common/Loaders";
import {
    resolveCandidateApplicationStatusTestBadgeClassNames,
} from "helpers/callDetailsCandidateApplicationStatusTestPresentation";

const pickFirst = (row = {}, keys = []) => {
    for (const key of keys) {
        const value = row?.[key];
        if (value === null || value === undefined) continue;
        const text = String(value).trim();
        if (text && text.toLowerCase() !== "null") return value;
    }
    return null;
};

/** Screenshot format: `30-Apr-2026 08:33 AM` */
const formatScheduledTableDateTime = (raw) => {
    const value = String(raw ?? "").trim();
    if (!value || value.toLowerCase() === "null") return "";
    const parsed = dayjs(raw);
    return parsed.isValid() ? parsed.format("DD-MMM-YYYY hh:mm A") : value;
};

const displayCell = (value) => {
    if (value === null || value === undefined || value === "") return "";
    return String(value);
};

const isHexColor = (value) => /^#[0-9A-Fa-f]{3,8}$/.test(String(value ?? "").trim());

const normalizeStatusToken = (value) =>
    String(value ?? "")
        .trim()
        .toLowerCase()
        .replace(/[\s_-]+/g, "");

/** Fallback when API does not send statusColorKey / rowHighlightKey. */
const deriveAiScheduleStatusColorKey = (label) => {
    const token = normalizeStatusToken(label);
    if (!token) return "neutral";

    if (
        token.includes("nottriggered") ||
        token.includes("pending") ||
        token.includes("firstcall") ||
        token.includes("scheduled")
    ) {
        return "warning";
    }
    if (
        token.includes("triggered") ||
        token.includes("complete") ||
        token.includes("success") ||
        token.includes("done")
    ) {
        return "success";
    }
    if (
        token.includes("fail") ||
        token.includes("error") ||
        token.includes("cancel") ||
        token.includes("reject")
    ) {
        return "danger";
    }
    if (token.includes("progress") || token.includes("processing")) {
        return "progress";
    }
    return "neutral";
};

/**
 * Shape AI schedule row fields so application-status badge helpers can resolve colors
 * (`statusColorKey` / `rowHighlightKey`, same as candidate-tests presentation).
 */
const toStatusPresentationRow = (
    row = {},
    labelKeys = [],
    colorKeyKeys = [],
    { preferApiColorKeyOnly = false } = {}
) => {
    const label = pickFirst(row, labelKeys);
    const apiStatusColorKey = pickFirst(row, colorKeyKeys);
    const apiRowHighlightKey = pickFirst(row, [
        "rowHighlightKey",
        "statusHighlightKey",
    ]);

    const derivedKey = preferApiColorKeyOnly
        ? "neutral"
        : deriveAiScheduleStatusColorKey(label);
    const statusColorKey =
        apiStatusColorKey != null && !isHexColor(apiStatusColorKey)
            ? String(apiStatusColorKey)
            : derivedKey;
    const rowHighlightKey =
        preferApiColorKeyOnly
            ? statusColorKey
            : apiRowHighlightKey != null
              ? String(apiRowHighlightKey)
              : statusColorKey;

    return {
        statusColorKey,
        rowHighlightKey,
        statusLabel: label != null ? String(label) : "",
        rawStatus: label != null ? String(label) : "",
        displayRawStatus: label != null ? String(label) : "",
    };
};

const renderStatusBadge = (
    row,
    { labelKeys, colorKeyKeys = [], preferApiColorKeyOnly = false } = {}
) => {
    const rawLabel = pickFirst(row, labelKeys);
    if (rawLabel == null || rawLabel === "") return "";

    const label = String(rawLabel);
    const hexColor = pickFirst(row, colorKeyKeys);

    if (isHexColor(hexColor)) {
        return (
            <Badge
                pill
                className="badge-padding cd-ai-call-scheduled-status-badge"
                style={{ backgroundColor: String(hexColor).trim(), color: "#fff" }}
            >
                {label}
            </Badge>
        );
    }

    const presentationRow = toStatusPresentationRow(row, labelKeys, colorKeyKeys, {
        preferApiColorKeyOnly,
    });
    const badgeClassNames = resolveCandidateApplicationStatusTestBadgeClassNames(
        presentationRow
    );

    return (
        <Badge
            pill
            className={`${badgeClassNames} cd-ai-call-scheduled-status-badge`}
        >
            {label}
        </Badge>
    );
};

export const extractAiCallScheduledRowsFromApiResponse = (apiResponse) => {
    const payload = apiResponse?.data ?? apiResponse;
    if (Array.isArray(payload)) return payload;
    if (!payload || typeof payload !== "object") return [];
    const nested = payload?.data;
    if (Array.isArray(nested)) return nested;
    for (const key of ["records", "items", "rows", "schedules", "aiCallScheduled", "list"]) {
        if (Array.isArray(payload[key])) return payload[key];
    }
    return [];
};

const TABLE_COLUMNS = [
    {
        key: "stageId",
        label: "Stage",
        render: (row) => displayCell(pickFirst(row, ["stageId"])),
    },
    {
        key: "scheduledStatusId",
        label: "Scheduled Status",
        render: (row) =>
            renderStatusBadge(row, {
                labelKeys: ["scheduledStatusId"],
                colorKeyKeys: ["scheduledStatusColorKey", "statusColorKey", "statusColor"],
            }),
    },
    {
        key: "actionType",
        label: "Action Type",
        render: (row) => displayCell(pickFirst(row, ["actionType"])),
    },
    {
        key: "scheduledTimeUtc",
        label: "Scheduled (UTC)",
        render: (row) => formatScheduledTableDateTime(pickFirst(row, ["scheduledTimeUtc"])),
    },
    {
        key: "scheduledTimeCandidateLocal",
        label: "Candidate Time",
        render: (row) =>
            formatScheduledTableDateTime(pickFirst(row, ["scheduledTimeCandidateLocal"])),
    },
    {
        key: "actionStatus",
        label: "Action Status",
        render: (row) =>
            renderStatusBadge(row, {
                labelKeys: ["actionStatus"],
                colorKeyKeys: ["actionStatusColorKey"],
                preferApiColorKeyOnly: true,
            }),
    },
    {
        key: "attemptNo",
        label: "Attempt",
        render: (row) => {
            const attempt = pickFirst(row, ["attemptNo", "attemptNumber", "attempt"]);
            return attempt != null && attempt !== "" ? String(attempt) : "0";
        },
    },
];

const isLastRow = (row = {}) => {
    const value = row?.isLast;
    if (value === true || value === 1) return true;
    const text = String(value ?? "").trim().toLowerCase();
    return text === "1" || text === "true" || text === "yes";
};

const CallDetailsAiCallScheduledModal = ({
    isOpen,
    onClose,
    loading = false,
    rows = [],
    candidateName = "",
}) => {
    const rowCount = rows.length;

    const subtitle = useMemo(() => {
        if (!candidateName) return `${rowCount} record${rowCount === 1 ? "" : "s"}`;
        return `${candidateName} · ${rowCount} record${rowCount === 1 ? "" : "s"}`;
    }, [candidateName, rowCount]);

    return (
        <Modal
            isOpen={isOpen}
            toggle={onClose}
            backdrop="static"
            size="xl"
            centered
            zIndex={5000}
            className="modal-full-height question-type-modal cd-ai-call-scheduled-modal"
        >
            <ModalHeader toggle={onClose} className="cd-ai-call-scheduled-modal__header">
                <div className="d-flex flex-column gap-1">
                    <span className="cd-ai-call-scheduled-modal__title">
                        <i className="mdi mdi-calendar-clock-outline me-2" aria-hidden />
                        AI Call Schedule Data
                    </span>
                    {/* <span className="cd-ai-call-scheduled-modal__subtitle text-muted small">
                        {subtitle}
                    </span> */}
                </div>
            </ModalHeader>
            <ModalBody className="cd-ai-call-scheduled-modal__body">
                {loading ? (
                    <div className="py-5 text-center">
                        <Loaders />
                    </div>
                ) : rowCount === 0 ? (
                    <div className="cd-ai-call-scheduled-empty text-center py-5 px-3">
                        <div className="cd-ai-call-scheduled-empty__icon mx-auto mb-3" aria-hidden>
                            <i className="mdi mdi-calendar-remove-outline" />
                        </div>
                        <h6 className="fw-semibold mb-1">No AI call schedule data</h6>
                        <p className="text-muted small mb-0">
                            Scheduled AI call records will appear here when available.
                        </p>
                    </div>
                ) : (
                    <div className="cd-ai-call-scheduled-table-wrap table-responsive">
                        <Table
                            bordered
                            className="cd-ai-call-scheduled-table mb-0"
                            responsive
                        >
                            <thead>
                                <tr>
                                    {TABLE_COLUMNS.map((col) => (
                                        <th key={col.key} scope="col" className="cd-ai-call-scheduled-th">
                                            {col.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row, idx) => {
                                    const rowKey =
                                        String(row?.id ?? row?._id ?? `ai-schedule-${idx}`).trim() ||
                                        `ai-schedule-${idx}`;
                                    const highlighted = isLastRow(row);
                                    return (
                                        <tr
                                            key={rowKey}
                                            className={
                                                highlighted
                                                    ? "cd-ai-call-scheduled-row--is-last"
                                                    : undefined
                                            }
                                        >
                                            {TABLE_COLUMNS.map((col) => (
                                                <td
                                                    key={col.key}
                                                    className={
                                                        col.wrap
                                                            ? "cd-ai-call-scheduled-td cd-ai-call-scheduled-td--wrap"
                                                            : "cd-ai-call-scheduled-td"
                                                    }
                                                >
                                                    {col.render(row)}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>
                )}
            </ModalBody>
        </Modal>
    );
};

export default CallDetailsAiCallScheduledModal;
